import React from 'react'
import {
  CheckCircleOutlined,
  EditOutlined,
  EyeOutlined,
  LockOutlined,
  UnlockOutlined,
} from '@ant-design/icons'
import {
  Button,
  Popconfirm,
  Space,
  Table,
  TableProps,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import { ColumnsType, ColumnType } from 'antd/es/table'
import { Link } from 'react-router-dom'
import { GUARD_RULES } from 'src/features/session/constants'
import { Guard } from 'src/features/session/guard'
import { path } from '../../../libs/path'
import { WalletGuardWallet } from '../types'
import {
  formatWalletDate,
  WALLET_STATUS_COLORS,
  WALLET_STATUS_LABELS,
  WALLET_TYPE_LABELS,
} from './config'

type Props = {
  wallets: WalletGuardWallet[]
  total: number
  loading: boolean
  page: number
  pageSize: number
  sortField?: string
  sortOrder?: string
  onChange: TableProps<WalletGuardWallet>['onChange']
  pendingWalletId: number | null
  onBlock: (id: number) => void
  onUnblock: (id: number) => void
}

export const WalletsTable = ({
  wallets,
  total,
  loading,
  page,
  pageSize,
  sortField,
  sortOrder,
  onChange,
  pendingWalletId,
  onBlock,
  onUnblock,
}: Props) => {
  const sortOrderFor = (
    field: keyof WalletGuardWallet,
  ): ColumnType<WalletGuardWallet>['sortOrder'] =>
    sortField === field && (sortOrder === 'ascend' || sortOrder === 'descend')
      ? sortOrder
      : null

  const columns: ColumnsType<WalletGuardWallet> = [
    {
      title: 'Адрес',
      dataIndex: 'address',
      key: 'address',
      width: 310,
      sorter: true,
      sortOrder: sortOrderFor('address'),
      render: (address: string) => (
        <Typography.Text copyable style={{ overflowWrap: 'anywhere' }}>
          {address}
        </Typography.Text>
      ),
    },
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      sorter: true,
      sortOrder: sortOrderFor('name'),
    },
    {
      title: 'Валюта',
      dataIndex: 'currency',
      key: 'currency',
      width: 110,
      sorter: true,
      sortOrder: sortOrderFor('currency'),
    },
    {
      title: 'Баланс',
      dataIndex: 'balance',
      key: 'balance',
      width: 160,
      align: 'right',
      sorter: true,
      sortOrder: sortOrderFor('balance'),
      render: (balance: string | null) => (
        <Typography.Text style={{ fontVariantNumeric: 'tabular-nums' }}>
          {balance ?? '—'}
        </Typography.Text>
      ),
    },
    {
      title: 'Тип',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      sorter: true,
      sortOrder: sortOrderFor('type'),
      render: (type: string) => WALLET_TYPE_LABELS[type] ?? type,
    },
    {
      title: 'Дата проверки',
      dataIndex: 'lastCheckedAt',
      key: 'lastCheckedAt',
      width: 190,
      sorter: true,
      sortOrder: sortOrderFor('lastCheckedAt'),
      render: formatWalletDate,
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 180,
      sorter: true,
      sortOrder: sortOrderFor('status'),
      render: (status: string) => (
        <Tag color={WALLET_STATUS_COLORS[status]}>
          {WALLET_STATUS_LABELS[status] ?? status}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 160,
      render: (_, wallet) => {
        const isBlocked = wallet.status === 'BLOCKED'
        const isPending = pendingWalletId === wallet.id
        const isDisabled = pendingWalletId !== null && !isPending

        return (
          <Space size={4}>
            <Guard
              permission={GUARD_RULES.VIEW_CRYPTO_WALLET}
              mode="permission"
            >
              <Tooltip title="Просмотр">
                <Link to={path.walletGuard.walletDetails(String(wallet.id))}>
                  <Button type="text" size="small" icon={<EyeOutlined />} />
                </Link>
              </Tooltip>
            </Guard>
            <Tooltip title="Проверить вручную">
              <Button type="text" size="small" icon={<CheckCircleOutlined />} />
            </Tooltip>
            <Guard
              permission={GUARD_RULES.EDIT_CRYPTO_WALLET}
              mode="permission"
            >
              <Tooltip title="Редактировать">
                <Link to={path.walletGuard.walletEdit(String(wallet.id))}>
                  <Button type="text" size="small" icon={<EditOutlined />} />
                </Link>
              </Tooltip>
            </Guard>
            {isBlocked ? (
              <Popconfirm
                title="Разблокировать этот кошелёк?"
                okText="Разблокировать"
                cancelText="Отмена"
                onConfirm={() => onUnblock(wallet.id)}
              >
                <Tooltip title="Разблокировать">
                  <Button
                    type="text"
                    size="small"
                    icon={<UnlockOutlined />}
                    loading={isPending}
                    disabled={isDisabled}
                  />
                </Tooltip>
              </Popconfirm>
            ) : (
              <Popconfirm
                title="Заблокировать этот кошелёк?"
                okText="Заблокировать"
                cancelText="Отмена"
                onConfirm={() => onBlock(wallet.id)}
              >
                <Tooltip title="Заблокировать">
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<LockOutlined />}
                    loading={isPending}
                    disabled={isDisabled}
                  />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        )
      },
    },
  ]

  return (
    <Table<WalletGuardWallet>
      rowKey="id"
      columns={columns}
      dataSource={wallets}
      loading={loading}
      bordered
      scroll={{ x: 1480 }}
      locale={{ emptyText: 'Кошельки не найдены' }}
      pagination={{
        current: page,
        pageSize,
        total,
        showSizeChanger: true,
        pageSizeOptions: [10, 20, 50, 100],
        showTotal: (total, range) => `${range[0]}–${range[1]} из ${total}`,
      }}
      onChange={onChange}
    />
  )
}
